import './printModal.scss'
import templateUrl from './printModal.html'

/**
 * Configures a print Modal using $mdPanel
 *
 * @type {ngConfig}
 */
function tmrPrintModalConfig($mdPanelProvider) {
  'ngInject'
  $mdPanelProvider.definePreset('printModal', {
    templateUrl,
    controller: 'tmrPrintModalController',
    controllerAs: '$ctrl',
    panelClass: 'tmr-modal tmr-print-modal md-whiteframe-1dp',
    clickOutsideToClose: true,
    escapeToClose: true,
  })
}

/**
 * Simplifies the usage of the {@link TmrPrintModal printModal panel}
 *
 * @example <caption>In a controller</caption>
 * this.TmrPrintModal($event, {
 *   resource: this.resource
 * });
 *
 * @alias TmrPrintModal
 * @type {ngFactory}
 *
 * @prop {object} openFrom The click event passed by the template
 * @prop {function} onPrint A callback that is executed when printing is started
 * @prop {TmrResource} options.resource The resource to be printed
 * @prop {number} options.requested The number of items requested for print
 * @prop {number} options.printed The number of printed items
 * @prop {boolean=} [options.askQuantity=true] If false, no quantity field is shown and quantity is always 1.
 * @prop {string=} options.relativeTo A selector of the element that governs the panel position
 */
function TmrPrintModalFactory($mdPanel) {
  'ngInject'

  return (
    openFrom,
    onPrint,
    { resource, requested, printed, askQuantity, relativeTo = openFrom.target }
  ) => {
    let position = $mdPanel
      .newPanelPosition()
      .relativeTo(relativeTo)
      .addPanelPosition($mdPanel.xPosition.CENTER, $mdPanel.yPosition.BELOW)
      .withOffsetY('.5rem')
    let animation = $mdPanel
      .newPanelAnimation()
      .openFrom(openFrom)
      .withAnimation($mdPanel.animation.FADE)

    return $mdPanel.open('printModal', {
      openFrom,
      onDomAdded: () => openFrom.target.classList.add('md-accent'),
      onDomRemoved: () => openFrom.target.classList.remove('md-accent'),
      position,
      animation,
      locals: {
        printModalLocals: { resource, requested, printed, askQuantity },
      },
      resolve: {
        printModalPrinters: /*@ngInject*/ Printer => Printer.query().$promise,
      },
      onCloseSuccess: (MdPanelRef, reason) => {
        if (reason == 'PRINTMODAL_PRINT') onPrint()
      },
    })
  }
}

/**
 * Controller for the panel preset printModal
 *
 * @type {ngController}
 * @see TmrPrintModal
 */
export class tmrPrintModalController {
  static ngType = 'controller'

  constructor(
    $transitions,
    TmrToast,
    mdPanelRef,
    printModalLocals,
    printModalPrinters
  ) {
    'ngInject'
    Object.assign(this, {
      $transitions,
      TmrToast,
      mdPanelRef,
      printModalLocals,
      printModalPrinters,
    })
  }

  $onInit() {
    if (this.printModalPrinters.length == 1)
      this.printer = this.printModalPrinters[0]
    this.leftToPrint =
      this.printModalLocals.requested - this.printModalLocals.printed
    if (this.leftToPrint < 0) this.leftToPrint = 0
    this.quantity = this.leftToPrint || 1
    this.askQuantity = this.printModalLocals.askQuantity
    this.onBeforeTransitionCancel = this.$transitions.onBefore({}, () =>
      this.close()
    )
  }

  getPrinters() {
    return this.printModalPrinters.filter(printer =>
      printer.$labelIncludes(this.printerText)
    )
  }

  close(reason) {
    this.onBeforeTransitionCancel()
    return this.mdPanelRef.close(reason)
  }

  cancel() {
    return this.close('PRINTMODAL_CANCEL')
  }

  print() {
    return this.close('PRINTMODAL_PRINT')
      .then(() =>
        this.printModalLocals.resource.$print(this.printer, this.quantity)
      )
      .then(() => this.TmrToast.success('PRINTMODAL.SUCCESS'))
  }
}

export { tmrPrintModalConfig as config }
export const factory = ['TmrPrintModal', TmrPrintModalFactory]
